import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Business as SuppliersIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  fetchItems,
  fetchLowStockItems,
  fetchSuppliers,
} from '../../redux/slices/inventorySlice';
import ItemsList from '../inventory/ItemsList';
import SupplierManagement from '../inventory/SupplierManagement';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

export default function FacilityInventoryTab({ facilityId }) {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const {
    items = [],
    lowStockItems = [],
    suppliers = [],
    loading,
    error,
  } = useSelector((state) => state.inventory);

  useEffect(() => {
    if (!facilityId) {
      console.warn('FacilityInventoryTab: No facilityId provided');
      return;
    }
    
    // Fetch initial data for this facility
    dispatch(fetchItems(facilityId));
    dispatch(fetchLowStockItems(facilityId));
    dispatch(fetchSuppliers(facilityId));
  }, [dispatch, facilityId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    if (!facilityId) return;
    dispatch(fetchItems(facilityId));
    dispatch(fetchLowStockItems(facilityId));
    dispatch(fetchSuppliers(facilityId));
  };

  if (!facilityId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please save the facility first to manage inventory.
        </Alert>
      </Box>
    );
  }

  if (loading && !items.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Inventory Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage items, suppliers, and stock levels for this facility
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleRefresh} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {lowStockItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{lowStockItems.length} item(s)</strong> are running low on stock
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, flex: '1 1 150px', minWidth: 150 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InventoryIcon color="primary" />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{items.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Items</Typography>
            </Box>
          </Box>
        </Paper>
        <Paper sx={{ p: 2, flex: '1 1 150px', minWidth: 150 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShippingIcon color="warning" />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{lowStockItems.length}</Typography>
              <Typography variant="body2" color="text.secondary">Low Stock</Typography>
            </Box>
          </Box>
        </Paper>
        <Paper sx={{ p: 2, flex: '1 1 150px', minWidth: 150 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SuppliersIcon color="info" />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{suppliers.length}</Typography>
              <Typography variant="body2" color="text.secondary">Suppliers</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper sx={{ borderRadius: '8px' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500
            }
          }}
        >
          <Tab icon={<InventoryIcon />} iconPosition="start" label="Items" />
          <Tab icon={<SuppliersIcon />} iconPosition="start" label="Suppliers" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <ItemsList items={items} facilityId={facilityId} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <SupplierManagement facilityId={facilityId} />
        </TabPanel>
      </Paper>
    </Box>
  );
}

